/** Shared read-only primitives for the booking & order overview panels:
 *  compact-grid props, resource links, timestamp + contact cells, money
 *  formatting. Kept tiny and presentational so both feature overviews reuse
 *  them without cross-feature coupling.
 *
 *  The link cells take an optional `ctx` (the owning record's tenant); with it
 *  they gain the booking value popovers (`bookingLinks`), without it they stay
 *  the plain links they always were. */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Link, Stack, Tooltip } from '@mui/material';
import type { DataGridProProps } from '@mui/x-data-grid-pro';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingAttachmentRef, BookingTag, NamedRef, UserRef } from '@/api/bookings';
import { TagBadgeSet } from '@/components/TagBadgeSet/TagBadgeSet';
import { tagChipActions, type TagChipContext } from '@/components/TagBadgeSet/tagChipActions';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { formatDate, formatRelativeTime } from '@/utils/format';

import {
  emailActions,
  guestActions,
  phoneActions,
  tourActions,
  type BookingCtx,
} from './bookingLinks';

/** Common props for the compact, read-only embedded grids (sx applied
 *  separately so grids can merge their own rules). */
export const READONLY_GRID_PROPS = {
  autoHeight: true,
  density: 'compact',
  hideFooter: true,
  disableColumnMenu: true,
  disableColumnFilter: true,
  disableRowSelectionOnClick: true,
  getRowHeight: () => 'auto' as const,
} satisfies Partial<DataGridProProps>;

export const READONLY_GRID_SX = {
  '& .MuiDataGrid-cell': { py: 0.75, alignItems: 'flex-start' },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
} as const;

export function userName(user: UserRef | null): string {
  if (!user) return '—';
  return `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || `#${user.id}`;
}

export function UserLink({ user, ctx }: { user: UserRef | null; ctx?: BookingCtx }) {
  if (!user) return <>—</>;
  const link = (
    <Link component={RouterLink} to={`/users/${user.id}`} underline="hover">
      {userName(user)}
    </Link>
  );
  return ctx ? <ValueActions actions={guestActions(user, ctx)}>{link}</ValueActions> : link;
}

export function TourLink({ tour, ctx }: { tour: NamedRef | null; ctx?: BookingCtx }) {
  if (!tour) return <>—</>;
  const link = (
    <Link component={RouterLink} to={`/tours/${tour.id}`} underline="hover">
      {tour.name ?? `#${tour.id}`}
    </Link>
  );
  return ctx ? <ValueActions actions={tourActions(tour, ctx)}>{link}</ValueActions> : link;
}

/** Absolute (default) or relative timestamp, with the raw ISO in a tooltip. */
export function Ts({ value, relative = false }: { value: string | null; relative?: boolean }) {
  if (!value) return <>—</>;
  return (
    <Tooltip title={value} arrow placement="top">
      <Box component="span">{relative ? formatRelativeTime(value) : formatDate(value)}</Box>
    </Tooltip>
  );
}

/** Main contacts as mailto/tel links, stacked. */
export function ContactLinks({
  email,
  phone,
  ctx,
}: {
  email: string | null;
  phone: string | null;
  ctx?: BookingCtx;
}) {
  if (!email && !phone) return <>—</>;
  const emailLink = email && (
    <Link href={`mailto:${email}`} underline="hover" variant="body2" sx={{ wordBreak: 'break-all' }}>
      {email}
    </Link>
  );
  const phoneLink = phone && (
    <Link href={`tel:${phone}`} underline="hover" variant="body2">
      {phone}
    </Link>
  );
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      {emailLink &&
        (ctx ? <ValueActions actions={emailActions(email, ctx)}>{emailLink}</ValueActions> : emailLink)}
      {phoneLink &&
        (ctx ? <ValueActions actions={phoneActions(phone, ctx)}>{phoneLink}</ValueActions> : phoneLink)}
    </Stack>
  );
}

/** Tag chips; with a `ctx` every chip offers the standard tag actions. */
export function TagsCell({
  tags,
  ctx,
  owner = 'booking',
}: {
  tags: BookingTag[];
  ctx?: BookingCtx;
  owner?: TagChipContext['owner'];
}) {
  return (
    <TagBadgeSet
      tags={tags}
      maxVisible={4}
      {...(ctx && { chipActions: (t: BookingTag) => tagChipActions(t, { tenantId: ctx.tenantId, owner }) })}
    />
  );
}

/** Small inline copy-to-clipboard icon button. */
export function CopyIconButton({ value, label = 'Value' }: { value: string; label?: string }) {
  const snackbar = useSnackbar();
  return (
    <Tooltip title={`Copy ${label.toLowerCase()}`}>
      <IconButton
        size="small"
        aria-label={`Copy ${label}`}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value).then(
            () => snackbar.show(`${label} copied.`, 'success'),
            () => snackbar.show('Could not copy to clipboard.', 'error'),
          );
        }}
        sx={{ p: 0.25 }}
      >
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
}

/** A monospace value with an inline copy button (em dash when empty). */
export function CopyableValue({
  value,
  label = 'Value',
  mono = true,
}: {
  value: string | null | undefined;
  label?: string;
  mono?: boolean;
}) {
  if (!value) return <>—</>;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, maxWidth: '100%' }}>
      <Box component="span" sx={{ ...(mono ? { fontFamily: 'monospace', fontSize: 13 } : {}), wordBreak: 'break-all' }}>
        {value}
      </Box>
      <CopyIconButton value={value} label={label} />
    </Box>
  );
}

/** A link (mailto/tel/external) with an inline copy button. */
export function CopyableLink({
  href,
  text,
  label = 'Value',
  external = false,
}: {
  href: string;
  text: string;
  label?: string;
  external?: boolean;
}) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, maxWidth: '100%' }}>
      <Link
        href={href}
        underline="hover"
        sx={{ wordBreak: 'break-all' }}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {text}
      </Link>
      <CopyIconButton value={text} label={label} />
    </Box>
  );
}

/** An attachment/voucher rendered as a short link (name → short_url, copy the URL). */
export function ShortLink({ att }: { att: BookingAttachmentRef }) {
  const href = att.short_url || att.url;
  if (!href) return <>—</>;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, maxWidth: '100%' }}>
      <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ wordBreak: 'break-all' }}>
        {att.name || 'Link'}
      </Link>
      <CopyIconButton value={href} label="link" />
    </Box>
  );
}

/** Minor-unit integer → major-unit display string (mirrors the Orders grid's
 *  formatMoney: currency code appended plainly since we only have the code). */
export function formatMoney(
  minor: number | null,
  currency: string | null,
  precision: number | null,
): string | null {
  if (minor == null) return null;
  const digits = precision ?? 2;
  const amount = (minor / 10 ** digits).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return currency ? `${amount} ${currency}` : amount;
}

/** Centered empty-state line for grids/sections with no rows. */
export function EmptyLine({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary', typography: 'body2' }}>
      {children}
    </Box>
  );
}
